import { ScribeQueueProvider } from './contexts/ScribeQueueContext';
/* eslint-disable import/first, @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { colors, gradients } from './theme/colors';
import { InactivityDetector } from './components/InactivityDetector/InactivityDetector';
import { InactivityWarningModal } from './components/InactivityDetector/InactivityWarningModal';

// Lazy-loaded page components
const LoginPage = React.lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegistrationWizard = React.lazy(() => import('./pages/registration/RegistrationWizard').then(m => ({ default: m.RegistrationWizard })));
// MedicalOfficerLoginPage is the legacy login — kept on disk but no longer routed
// (its routes are repointed to the new LoginPage). EmailVerificationPage remains
// routed at /verify-email for legacy emailed links.
const EmailVerificationPage = React.lazy(() => import('./pages/EmailVerificationPage').then(m => ({ default: m.EmailVerificationPage })));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const DoctorPage = React.lazy(() => import('./pages/DoctorPage').then(m => ({ default: m.DoctorPage })));
const DoctorDashboardPage = React.lazy(() => import('./pages/DoctorDashboardPage').then(m => ({ default: m.DoctorDashboardPage })));
const ManageSessionsPage = React.lazy(() => import('./pages/ManageSessionsPage').then(m => ({ default: m.ManageSessionsPage })));
const SuperAdminDashboardPage = React.lazy(() => import('./pages/SuperAdminDashboardPage').then(m => ({ default: m.SuperAdminDashboardPage })));
const AIUsageDashboardPage = React.lazy(() => import('./pages/AIUsageDashboardPage').then(m => ({ default: m.AIUsageDashboardPage })));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const ClinicProfilePage = React.lazy(() => import('./pages/ClinicProfilePage').then(m => ({ default: m.ClinicProfilePage })));
const ClinicAdministrationPage = React.lazy(() => import('./pages/ClinicAdministrationPage').then(m => ({ default: m.ClinicAdministrationPage })));
const SchedulingPage = React.lazy(() => import('./pages/SchedulingPage').then(m => ({ default: m.SchedulingPage })));
const ProtocolsPage = React.lazy(() => import('./pages/ProtocolsPage').then(m => ({ default: m.ProtocolsPage })));
const MeetingRoomPage = React.lazy(() => import('./pages/MeetingRoomPage').then(m => ({ default: m.MeetingRoomPage })));
const WorkflowTestHarness = React.lazy(() => import('./pages/WorkflowTestHarness').then(m => ({ default: m.WorkflowTestHarness })));
const WorkflowApprovalHarness = React.lazy(() => import('./pages/WorkflowApprovalHarness').then(m => ({ default: m.WorkflowApprovalHarness })));
const ConsultLanguagePreviewPage = React.lazy(() => import('./pages/ConsultLanguagePreviewPage').then(m => ({ default: m.ConsultLanguagePreviewPage })));
const TKRDashboardPage = React.lazy(() => import('./pages/TKRDashboardPage'));
const TKREpisodePage = React.lazy(() => import('./pages/TKREpisodePage'));
const SDMConsultationPage = React.lazy(() => import('./pages/SDMConsultationPage').then(m => ({ default: m.SDMConsultationPage })));
const VBCDashboardPage = React.lazy(() => import('./pages/VBCDashboardPage').then(m => ({ default: m.VBCDashboardPage })));

import { storage } from './utils/storage';
import { isSubscriptionActive } from './utils/entitlements';
import { SubscriptionPendingBlock } from './components/SubscriptionPendingBlock/SubscriptionPendingBlock';
import { authService } from './services/api/authService';
import { analyticsService } from './services/analyticsService';
import { isTokenExpired } from './utils/jwtUtils';
import { environment } from './config/environment';
import { ROUTES } from './constants/routes';
import { useI18n } from './i18n';

import './App.scss';

// Protected Route wrapper component with role-based access control
const ProtectedRoute = ({
  children,
  isLoggedIn,
  requiredRole
}: {
  children: React.ReactElement,
  isLoggedIn: boolean,
  requiredRole?: 'admin' | 'medical_officer' | 'super_admin' | 'clinic_staff' | 'any'
}) => {
  if (!isLoggedIn) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // If a specific role is required, check user permissions
  if (requiredRole && requiredRole !== 'any') {
    const authData = storage.getAuthData();
    if (!authData || !authData.user) {
      return <Navigate to={ROUTES.LOGIN} replace />;
    }

    const user = authData.user;
    const isAdmin = user.is_admin;
    const isMedicalOfficer = user.is_medical_officer;
    const isSuperAdmin = user.is_super_admin;
    const isNurse = user.is_nurse === true;
    const hasBothRoles = isAdmin && isMedicalOfficer && !isSuperAdmin;

    /*
     * `clinic_staff` — any clinic role, checked BEFORE the role-specific
     * branches below so a nurse is not bounced by the `hasBothRoles` shortcut
     * or by `admin`'s redirect.
     *
     * It exists for the scheduling workspace, whose backend grants
     * `can_view_calendar` to admins, medical officers and nurses alike
     * (`SchedulingCapabilitiesService.report`). The old `admin` guard was
     * narrower than the endpoint it fronted, so a doctor could not open the
     * calendar the server was willing to show them.
     *
     * Presentation only. Every scheduling endpoint re-checks the role: reads
     * admit clinic staff, and a WRITE is `IsMedicalOfficer`, so widening this
     * route cannot widen what anybody may book.
     */
    if (requiredRole === 'clinic_staff') {
      if (isAdmin || isMedicalOfficer || isNurse || isSuperAdmin) {
        return children;
      }
      return <Navigate to={ROUTES.LOGIN} replace />;
    }

    // Users with both admin and medical officer roles can access both admin and doctor routes
    if (hasBothRoles) {
      // But they cannot access super admin routes
      if (requiredRole === 'super_admin') {
        return <Navigate to={ROUTES.DOCTOR_DASHBOARD} replace />;
      }
      return children;
    }

    // Check if user has the required role
    if (requiredRole === 'super_admin' && !isSuperAdmin) {
      // Non-super admins trying to access super admin routes
      if (isAdmin) {
        return <Navigate to={ROUTES.DOCTOR_DASHBOARD} replace />;
      } else if (isMedicalOfficer) {
        return <Navigate to={ROUTES.DOCTOR_DASHBOARD} replace />;
      } else {
        return <Navigate to={ROUTES.LOGIN} replace />;
      }
    }

    if (requiredRole === 'admin' && !isAdmin) {
      // Only users with admin role (not super admin) can access admin routes
      if (isSuperAdmin) {
        return <Navigate to={ROUTES.SUPER_ADMIN_DASHBOARD} replace />;
      }
      // Medical officers (without admin role) trying to access admin routes
      return <Navigate to={ROUTES.DOCTOR_DASHBOARD} replace />;
    }

    if (requiredRole === 'medical_officer' && !isMedicalOfficer) {
      // SuperAdmin with a selected clinic can view doctor pages (read-only)
      if (isSuperAdmin && user.clinic_id) {
        return children;
      }
      if (isSuperAdmin) {
        return <Navigate to={ROUTES.SUPER_ADMIN_DASHBOARD} replace />;
      }
      // Admins (without medical officer role) trying to access doctor routes
      return <Navigate to={ROUTES.DOCTOR_DASHBOARD} replace />;
    }
  }

  // Subscription gate: a medical officer whose clinic has no active subscription
  // is allowed to authenticate (so invited doctors can finish password + MFA
  // setup) but is blocked from the app until the clinic subscribes. Admins,
  // super-admins, and non-medical-officers bypass (see isSubscriptionActive).
  if (!isSubscriptionActive()) {
    return <SubscriptionPendingBlock />;
  }

  return children;
};


function App() {
  const { t } = useI18n();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [redirectPath, setRedirectPath] = useState<string>(ROUTES.DOCTOR_DASHBOARD);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const authData = storage.getAuthData();

      if (!authData) {
        setIsLoggedIn(false);
        storage.clearAuthData();
        setIsLoading(false);
        return;
      }

      // Check if the access token is expired
      if (isTokenExpired(authData.access_token)) {
        try {
          const refreshToken = authData.refresh_token;
          if (!refreshToken) {
            storage.clearAll();
            setIsLoading(false);
            return;
          }

          const response = await fetch(`${environment.LOGIN_API_URL}/refreshtoken`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              grant_type: 'refresh_token',
              refresh_token: refreshToken,
              client_id: environment.CLIENT_ID,
            }),
          });

          if (!response.ok) {
            storage.clearAll();
            setIsLoading(false);
            return;
          }

          const data = await response.json();
          storage.setAuthData({
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            expires_in: data.expires_in,
            user: authData.user,
            registration_status: authData.registration_status,
          });
        } catch {
          storage.clearAll();
          setIsLoading(false);
          return;
        }
      }

      setIsLoggedIn(true);

      // Determine correct redirect path based on user role
      const user = authData.user;

      // Identify user in analytics
      if (user) {
        analyticsService.identify(user.email, {
          email: user.email,
          name: user.name,
          clinic_id: user.clinic_id,
          is_admin: user.is_admin,
          is_medical_officer: user.is_medical_officer,
          is_super_admin: user.is_super_admin,
        });
      }
      const registrationStatus = authData.registration_status;
      const isAdmin = user && user.is_admin;
      const isMedicalOfficer = user && user.is_medical_officer;
      const isSuperAdmin = user && user.is_super_admin;
      const hasBothRoles = isAdmin && isMedicalOfficer;

      // Super admins always go to super admin dashboard (never registration)
      if (isSuperAdmin) {
        // If SuperAdmin has a clinic selected (from previous session), go to doctor view
        if (user && user.clinic_id) {
          setRedirectPath(ROUTES.MANAGE_SESSIONS);
        } else {
          setRedirectPath(ROUTES.SUPER_ADMIN_DASHBOARD);
        }
      // Users with both roles default to doctor dashboard
      } else if (hasBothRoles) {
        if (registrationStatus && !registrationStatus.is_registration_complete) {
          // Partially-registered users resume the new registration wizard.
          setRedirectPath(ROUTES.REGISTER);
        } else {
          setRedirectPath(ROUTES.DOCTOR_DASHBOARD);
        }
      } else if (isAdmin) {
        if (registrationStatus && !registrationStatus.is_registration_complete) {
          // Partially-registered users resume the new registration wizard.
          setRedirectPath(ROUTES.REGISTER);
        } else {
          setRedirectPath(ROUTES.DOCTOR_DASHBOARD);
        }
      } else {
        setRedirectPath(ROUTES.DOCTOR_DASHBOARD);
      }

      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const handleLoginSuccess = (userType: 'medical_officer' | 'admin_registration' | 'admin_dashboard' | 'super_admin_dashboard') => {
    // Determine redirect path first
    let path: string = ROUTES.MANAGE_SESSIONS;
    if (userType === 'admin_registration') {
       path = ROUTES.ADMIN_REGISTRATION;
    } else if (userType === 'super_admin_dashboard') {
     path = ROUTES.SUPER_ADMIN_DASHBOARD;
    } else if (userType === 'admin_dashboard') {
      path = ROUTES.DOCTOR_DASHBOARD;
  }
    // Update both states together in a single batch to prevent multiple re-renders
    // React 18 automatically batches these updates
    setRedirectPath(path);
    setIsLoggedIn(true);
  };

  const handleInactivityWarning = () => {
    setShowInactivityWarning(true);
  };

  const handleExtendSession = () => {
    setShowInactivityWarning(false);
    // The InactivityDetector will automatically reset its timers
    // when user activity is detected (clicking the button)
  };

  const handleInactivityTimeout = () => {
    setShowInactivityWarning(false);
    setIsLoggedIn(false);
  };

  const handleLogout = async () => {
    try {
      // authService.logout() now handles clearing all storage data
      await authService.logout();
      analyticsService.reset();
      setIsLoggedIn(false);
      setShowInactivityWarning(false);
    } catch (error) {
      console.error('Logout failed:', error);
      // Ensure cleanup even if logout API call fails
      storage.clearAll();
      analyticsService.reset();
      setIsLoggedIn(false);
      setShowInactivityWarning(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: gradients.card
      }}>
        <div style={{
          color: 'white',
          fontSize: '18px',
          fontWeight: '500'
        }}>
          {t('common.loading')}
        </div>
      </div>
    ); 
  }

  return (
    <BrowserRouter>
      <ScribeQueueProvider>
      <div data-component="App">
        {/* Inactivity Detection - only active when logged in */}
        {isLoggedIn && (
          <InactivityDetector
            timeoutInMinutes={61}  // Total timeout: 61 minutes (60 min + 1 min warning)
            warningInMinutes={60}  // Warning at 60 minutes (1 hour)
            onWarning={handleInactivityWarning}
            onTimeout={handleInactivityTimeout}
          />
        )}
        
        {/* Inactivity Warning Modal */}
        <InactivityWarningModal
          isOpen={showInactivityWarning}
          onExtend={handleExtendSession}
          onLogout={handleLogout}
          secondsRemaining={60}  // 1 minute warning countdown
        />
        
        <ErrorBoundary>
        <Suspense fallback={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            background: colors.background,
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid rgba(20, 184, 166, 0.2)',
              borderTop: `4px solid ${colors.primary}`,
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        }>
        <Routes>
          {/* Root redirect → new public login */}
          <Route
            path="/"
            element={<Navigate to={ROUTES.LOGIN_NEW} replace />}
          />

          {/* New public auth routes (redesigned flow) */}
          <Route
            path={ROUTES.LOGIN_NEW}
            element={
              isLoggedIn ?
                <Navigate to={redirectPath} replace /> :
                <LoginPage onLoginSuccess={handleLoginSuccess} />
            }
          />
          <Route
            path={ROUTES.REGISTER}
            element={(
              <RegistrationWizard
                onLoginSuccess={handleLoginSuccess}
                onLogout={handleLogout}
              />
            )}
          />

          {/* Legacy login route — repointed to the new login so emailed links
              and bookmarks keep working. */}
          <Route
            path={ROUTES.LOGIN}
            element={
              isLoggedIn ?
                <Navigate to={redirectPath} replace /> :
                <LoginPage onLoginSuccess={handleLoginSuccess} />
            }
          />
          <Route
            path={ROUTES.VERIFY_EMAIL}
            element={<EmailVerificationPage onLoginSuccess={handleLoginSuccess} />}
          />
          <Route
            path={ROUTES.FORGOT_PASSWORD}
            element={<ForgotPasswordPage />}
          />
          <Route
            path={ROUTES.DOCTOR_DASHBOARD}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="any">
                  <DoctorDashboardPage onLogout={handleLogout} />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.MANAGE_SESSIONS}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="any">
                  <ManageSessionsPage onLogout={handleLogout} />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.SESSION_VIEW}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="any">
                  <DoctorPage onLogout={handleLogout} />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={`${ROUTES.MEETING}/:meetingId`}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="medical_officer">
                  <MeetingRoomPage />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.PROFILE}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="any">
                  <ProfilePage onLogout={handleLogout} />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.CLINIC_PROFILE}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="admin">
                  <ClinicProfilePage onLogout={handleLogout} />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.CLINIC_ADMINISTRATION}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="admin">
                  <ClinicAdministrationPage onLogout={handleLogout} />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          {/* Scheduling is a whole-clinic workspace, not an admin screen. The
              backend grants `can_view_calendar` to admins, doctors and nurses
              alike, so an admin-only guard was narrower than the endpoint it
              fronted — a doctor could not open a calendar the server was
              willing to show them. The page re-checks the server's own
              `can_configure` before drawing anything editable and `can_book`
              before offering a booking; this guard is presentation only, and
              every write is re-checked server side. */}
          <Route
            path={ROUTES.SCHEDULING}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="clinic_staff">
                  <SchedulingPage />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          {/* The Protocols home, opened from Clinic Administration's Protocols button.
              Reading workflow series is open to clinic staff on the server (EHR-07);
              the classic list is for medical officers and super admins, so a clinic
              admin's library holds workflows only. Every edit, publish and release is
              re-checked on the server, so this guard is presentation only. Doctors reach the same workspace from their
              sessions page's Protocols tab. */}
          <Route
            path={ROUTES.PROTOCOLS}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="clinic_staff">
                  <ProtocolsPage onLogout={handleLogout} />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.ADMIN_REGISTRATION}
            element={<Navigate to={ROUTES.REGISTER} replace />}
          />
          <Route
            path={ROUTES.ADMIN_DASHBOARD}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="admin">
                  <Navigate to={ROUTES.CLINIC_ADMINISTRATION} replace />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.SUPER_ADMIN_DASHBOARD}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="super_admin">
                  <SuperAdminDashboardPage onLogout={handleLogout} />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          {/* AI Usage analytics — admin + super-admin (the page itself gates
              the role since ProtectedRoute's 'admin' bounces super-admins). */}
          <Route
            path={ROUTES.AI_USAGE_DASHBOARD}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="any">
                  <AIUsageDashboardPage />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />

          {/* TKR / TDABC / VBC routes */}
          <Route
            path={ROUTES.TKR_DASHBOARD}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="medical_officer">
                  <TKRDashboardPage />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.TKR_EPISODE}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="medical_officer">
                  <TKREpisodePage />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.TKR_SDM}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="medical_officer">
                  <SDMConsultationPage />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.VBC_DASHBOARD}
            element={
              <ErrorBoundary>
                <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="medical_officer">
                  <VBCDashboardPage />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />

         {/* Test harness route — dev/staging only, still requires auth */}
         {process.env.NODE_ENV !== 'production' && (
           <Route path={ROUTES.TEST_WORKFLOW} element={
             <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="medical_officer">
               <WorkflowTestHarness />
             </ProtectedRoute>
           } />
         )}
         {/* FE-07 approval basket host, dev/staging only, until FE-06's runner mounts it. Any
             signed-in role may open it: the basket itself shows who may decide (an acting-as
             super admin, a nurse or a clinic admin reads only), and the server re-checks. */}
         {process.env.NODE_ENV !== 'production' && (
           <Route path={ROUTES.TEST_WORKFLOW_APPROVAL} element={
             <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="any">
               <WorkflowApprovalHarness />
             </ProtectedRoute>
           } />
         )}

         {/* Dev-only host of the real AI Scribe language pickers (no auth). */}
         {process.env.NODE_ENV !== 'production' && environment.isDevelopment && (
           <Route
             path={ROUTES.CONSULT_LANGUAGE_PREVIEW}
             element={<ConsultLanguagePreviewPage />}
           />
         )}

         {/* Catch all redirect → new public login */}
         <Route
            path="*"
            element={<Navigate to={ROUTES.LOGIN_NEW} replace />}
          />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </div>
      </ScribeQueueProvider>
    </BrowserRouter>
  );
}

export default App;
